import React, { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { useQuery, useQueryClient } from "react-query";
import {
  callUseTicket,
  getTicketDetailById,
} from "../../services/game.service";
import ScratchCard from "react-scratchcard-v2";
import html2canvas from "html2canvas";
import safeHtml2canvas from "../../utils/safeHtml2canvas";
import CardGameItem from "../items/CardGameItem";
import { useNavigate } from "react-router-dom";

interface PopUpUseTicketProps {
  open: boolean;
  ticketId: string;
  onClose: () => void;
  onStartOpen: (data: any) => void;
}

export default function PopUpUseTicket({
  open,
  ticketId,
  onClose,
  onStartOpen,
}: PopUpUseTicketProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const divRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const scratchRef = useRef<any>(null);
  const pollingRef = useRef<number | null>(null);
  const prevPercentRef = useRef<number>(-1);
  const isAudioPlayingRef = useRef<boolean>(false);
  const [imageScratch, setImageScratch] = useState<string>("");
  const [resultData, setResultData] = useState<any>(null);
  const [closeButtonVisible, setCloseButtonVisible] = useState<boolean>(false);

  const { data: ticketDetail, isLoading } = useQuery(
    ["get-ticket-detail", ticketId],
    async () => {
      // getTicketDetailById() reads token from sessionStorage if not provided
      const response = await getTicketDetailById({ ticketId });
      return response;
    },
    {
      enabled: !!ticketId,
      onSuccess: (data) => {
        if (data?.isUsed || data?.isExpired || data?.isActive == false) {
          setCloseButtonVisible(true);
        } else {
          setCloseButtonVisible(false);
        }
      },
    }
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (divRef.current && open) {
        try {
          const dataUrl = await safeHtml2canvas(divRef.current, {
            scale: 2,
            backgroundColor: null,
          });
          if (!cancelled) setImageScratch(dataUrl || "");
          if (!dataUrl)
            console.warn(
              "PopUpUseTicket: html2canvas returned null (fallback)"
            );
        } catch (err) {
          console.warn("PopUpUseTicket: html2canvas failed", err);
          if (!cancelled) setImageScratch("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, ticketId, ticketDetail]);

  // If resultData exists but image generation failed, trigger fallback UX
  useEffect(() => {
    if (resultData && !imageScratch) {
      const t = setTimeout(() => {
        try {
          onStartOpen(resultData);
        } catch (e) {
          // ignore
        }
      }, 1200);
      return () => clearTimeout(t);
    }
  }, [resultData, imageScratch, onStartOpen]);

  // Poll scratch progress and update audio currentTime based on percent
  const startPolling = () => {
    if (pollingRef.current) return;
    // Reset tracking so the first read triggers an immediate update
    prevPercentRef.current = -1;

    const tick = () => {
      try {
        const pRaw = scratchRef.current?.getFilledInPixels?.(32) || 0;
        // Ensure percent is clamped to 0..100 (library may return percent or pixel count)
        const percent = Math.min(100, Math.max(0, pRaw));

        if (
          audioRef.current &&
          audioRef.current.duration &&
          !isNaN(audioRef.current.duration)
        ) {
          const duration = audioRef.current.duration;
          const target = (percent / 100) * duration;
          const current = audioRef.current.currentTime || 0;
          const diff = target - current;

          // Only update when percent changed enough or time difference is significant
          const percentChanged =
            Math.abs(percent - (prevPercentRef.current || 0)) >= 0.5; // 0.5% threshold
          const timeChanged = Math.abs(diff) > 0.25; // seconds

          if (percentChanged && timeChanged) {
            // Apply a small max step to avoid large instantaneous seeks (which cause stutter)
            const maxStep = 0.2; // seconds
            const newTime =
              Math.abs(diff) > maxStep
                ? current + Math.sign(diff) * maxStep
                : target;
            try {
              audioRef.current.currentTime = Math.max(
                0,
                Math.min(newTime, duration)
              );
            } catch (e) {
              // Setting currentTime may fail if audio not ready; ignore and try next tick
            }
            prevPercentRef.current = percent;
          }

          // Start playback once on first interaction (avoid calling play() every frame)
          if (!isAudioPlayingRef.current && audioRef.current.paused) {
            audioRef.current
              .play()
              .then(() => {
                isAudioPlayingRef.current = true;
              })
              .catch(() => {
                // play may be blocked until a user gesture; ignore and allow future attempts
              });
          }
        }
      } catch (err) {
        // ignore errors
      }

      pollingRef.current = requestAnimationFrame(tick);
    };

    pollingRef.current = requestAnimationFrame(tick);
  };

  const stopPolling = () => {
    if (pollingRef.current) cancelAnimationFrame(pollingRef.current);
    pollingRef.current = null;
    isAudioPlayingRef.current = false;
    try {
      audioRef.current?.pause();
    } catch (e) {
      // ignore
    }
  };

  // Preload the scratch sound effect and clean up when component unmounts
  useEffect(() => {
    audioRef.current = new Audio(
      "/sounds/scratch-sound-effect-for-video-title-underline.mp3"
    );
    audioRef.current.preload = "auto";
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        audioRef.current = null;
      }
    };
  }, []);

  const onSubmit = async () => {
    setLoading(true);
    try {
      console.log("onSubmit call onsubmit");
      // callUseTicket() reads token from sessionStorage if not provided
      const response = await callUseTicket({ ticketId });
      console.log("response:::", response);
      setResultData(response);
    } catch (error) {
      // errorAdd("ຊື້ບໍ່ສຳເລັດ"); // ถ้ามี sweetalert
      onClose();
    } finally {
      setLoading(false);
    }
  };

  // Ensure polling stops when modal closes or when resultData is set
  useEffect(() => {
    if (!open || resultData) {
      stopPolling();
    }
    return () => stopPolling();
  }, [open, resultData]);

  // Reset component state when the ticketId changes (new ticket selection)
  useEffect(() => {
    // stop everything and clear derived state for a fresh ticket
    stopPolling();
    prevPercentRef.current = -1;
    isAudioPlayingRef.current = false;
    setImageScratch("");
    setResultData(null);
    setLoading(false);
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    } catch (e) {
      // ignore
    }
  }, [ticketId]);

  if (isLoading || !ticketDetail) {
    return null;
  }

  return (
    <>
      {/* Hidden div to convert to image */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        {ticketDetail?.ticketNumber ? (
          <div
            ref={divRef}
            className="bg-[#D65072] w-[120px] h-[120px] text-white text-[50px] font-bold flex justify-center items-center rounded-[10px]"
          >
            {ticketDetail?.ticketNumber}
          </div>
        ) : (
          ""
        )}
      </div>

      <Modal
        show={open}
        onHide={() => {}}
        keyboard={false}
        style={{ border: "none" }}
        centered
        size="sm"
        contentClassName="border-[4px] border-[#D65072] shadow-none rounded-[30px] relative max-w-[280px] mx-auto"
      >
        <div className="absolute -top-[60px] left-0 w-full flex justify-center">
          <img
            src="/images/popup-texture-top.png"
            alt="texture"
            className="w-[90%]"
            style={{
              // Force GPU compositing on Android
              transform: "translate3d(0,0,0)",
              WebkitTransform: "translate3d(0,0,0)",
              willChange: "transform",
            }}
          />
        </div>
        <div className="flex justify-center items-center h-[280px]">
          {!resultData ? (
            <div className="bg-[#D65072] w-[120px] h-[120px] text-white text-[50px] font-bold flex justify-center items-center rounded-[10px]">
              {ticketDetail?.ticketNumber}
            </div>
          ) : (
            imageScratch && (
              <div
                onMouseDown={() => startPolling()}
                onTouchStart={() => startPolling()}
                onPointerDown={() => startPolling()}
                onMouseUp={() => stopPolling()}
                onTouchEnd={() => stopPolling()}
                onPointerUp={() => stopPolling()}
              >
                <ScratchCard
                  ref={scratchRef}
                  width={120}
                  height={120}
                  image={imageScratch}
                  finishPercent={80}
                  onComplete={() => {
                    console.log("complete");
                    // Stop polling when complete
                    stopPolling();
                    // Keep audio at the current proportional position; optionally pause after a short delay
                    if (audioRef.current) {
                      setTimeout(() => {
                        try {
                          audioRef.current?.pause();
                        } catch (e) {}
                      }, 500);
                      if (
                        resultData?.ticketDoc?.result_lv1?.value ==
                        "RESULT_MATCH"
                      ) {
                        setTimeout(() => {
                          navigate(
                            `/game2/select/${resultData?.ticketDoc?.result_lv1?.ticketId}`,
                            { replace: true }
                          );
                        }, 2000);
                      } else if (
                        resultData?.ticketDoc?.result_lv1?.value ==
                        "RESULT_NOT_MATCH"
                      ) {
                        setTimeout(() => {
                          // setCloseButtonVisible(true);
                          navigate(`/game2/select/undifined`, {
                            replace: false,
                          });
                        }, 2000);
                      } else {
                        onStartOpen(resultData);
                      }
                    }
                  }}
                >
                  <CardGameItem
                    width={120}
                    height={120}
                    itemKey={
                      resultData?.ticketDoc?.result_lv1?.value == "RESULT_MATCH"
                        ? resultData?.ticketResult?.lv2Type
                        : "panda"
                    }
                  />
                </ScratchCard>
              </div>
            )
          )}
        </div>
        <Modal.Footer
          className="absolute -bottom-[80px] left-0 w-full flex justify-center border-0 bg-transparent"
          hidden={!!resultData}
        >
          <Button
            variant="primary"
            onClick={onSubmit}
            disabled={loading}
            className="border-[#ffffff] border-[2px] rounded-[20px] px-8 py-2 font-bold text-lg hover:border-[#ffffff]"
          >
            {"ເປີດສະຫລາກ"}
          </Button>
        </Modal.Footer>

        <Modal.Footer
          className="absolute -bottom-[80px] left-0 w-full flex justify-center border-0 bg-transparent"
          hidden={!closeButtonVisible}
        >
          <Button
            variant="primary"
            onClick={onClose}
            disabled={loading}
            className="border-[#ffffff] border-[2px] rounded-[20px] px-8 py-2 font-bold text-lg hover:border-[#ffffff]"
          >
            {"ປິດ"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
